import { z } from "zod"
import { AddressIdSchema } from "./common"
import { IsoTimestampSchema } from "./string-validation"
import { createRequiredPersonNameSchema } from "./person-name"
import { UsStateSchema } from "./intake-sanity"
import { AddressSchema } from "./patient"

export const RestrictedStatesSchema = z.array(UsStateSchema).max(50).refine(states => new Set(states).size === states.length, "States must not be duplicated")
export const RestrictedStateSettingsSchema = z.strictObject({ restrictedStates: RestrictedStatesSchema, updatedAt: IsoTimestampSchema }).meta({ id: "RestrictedStateSettings" })
export const UpdateRestrictedStateSettingsSchema = RestrictedStateSettingsSchema.pick({ restrictedStates: true }).meta({ id: "UpdateRestrictedStateSettings" })

export function isRestrictedShippingState(state: string, restrictedStates: readonly string[]): boolean {
	return restrictedStates.includes(state.trim().toUpperCase())
}

export const ShippingStateOverrideSchema = z.strictObject({
	addressId: AddressIdSchema,
	state: UsStateSchema,
	addressUpdatedAt: IsoTimestampSchema,
	approvedBy: z.uuid(),
	approvedAt: IsoTimestampSchema,
	reason: z.string().trim().min(1).max(2000),
}).meta({ id: "ShippingStateOverride" })
export type ShippingStateOverride = z.infer<typeof ShippingStateOverrideSchema>

export function matchesShippingStateOverride(approval: ShippingStateOverride | null, address: {
	readonly id: string
	readonly region: string
	readonly updatedAt: Date
}): boolean {
	return approval !== null && approval.addressId === address.id &&
		approval.state === address.region.trim().toUpperCase() &&
		approval.addressUpdatedAt === address.updatedAt.toISOString()
}

export const ShippingStateAccessSchema = z.strictObject({
	approvedAddressId: AddressIdSchema.nullable(),
	restrictedStates: RestrictedStatesSchema,
}).meta({ id: "ShippingStateAccess" })

export const PortalOrderShippingStateSchema = z.strictObject({
	addresses: z.array(AddressSchema),
	approval: ShippingStateOverrideSchema.nullable(),
	approverLabel: z.string().nullable(),
	approvedAddressId: AddressIdSchema.nullable(),
	restrictedStates: RestrictedStatesSchema,
}).meta({ id: "PortalOrderShippingState" })

export const ApproveShippingStateOverrideRequestSchema = z.strictObject({
	recipientFirstName: createRequiredPersonNameSchema("First name"),
	recipientLastName: createRequiredPersonNameSchema("Last name"),
	addressId: AddressIdSchema,
	reason: z.string().trim().min(1, "An exception reason is required.").max(2000),
}).meta({ id: "ApproveShippingStateOverrideRequest" })

export const AppointmentStateOverrideSchema = z.strictObject({
	state: UsStateSchema,
	approvedBy: z.uuid(),
	approvedAt: IsoTimestampSchema,
	reason: z.string().trim().min(1).max(2000),
}).meta({ id: "AppointmentStateOverride" })

export const AppointmentStateAccessSchema = z.strictObject({
	approvedState: UsStateSchema.nullable(),
	restrictedStates: RestrictedStatesSchema,
}).meta({ id: "AppointmentStateAccess" })

export const PortalOrderAppointmentStateSchema = z.strictObject({
	approval: AppointmentStateOverrideSchema.nullable(),
	restrictedStates: RestrictedStatesSchema,
	approverLabel: z.string().nullable(),
}).meta({ id: "PortalOrderAppointmentState" })

export const ApproveAppointmentStateOverrideRequestSchema = AppointmentStateOverrideSchema.pick({ state: true, reason: true })
	.meta({ id: "ApproveAppointmentStateOverrideRequest" })

export const US_STATE_LABELS: Record<z.infer<typeof UsStateSchema>, string> = {
	AL: "Alabama",
	AK: "Alaska",
	AZ: "Arizona",
	AR: "Arkansas",
	CA: "California",
	CO: "Colorado",
	CT: "Connecticut",
	DE: "Delaware",
	FL: "Florida",
	GA: "Georgia",
	HI: "Hawaii",
	ID: "Idaho",
	IL: "Illinois",
	IN: "Indiana",
	IA: "Iowa",
	KS: "Kansas",
	KY: "Kentucky",
	LA: "Louisiana",
	ME: "Maine",
	MD: "Maryland",
	MA: "Massachusetts",
	MI: "Michigan",
	MN: "Minnesota",
	MS: "Mississippi",
	MO: "Missouri",
	MT: "Montana",
	NE: "Nebraska",
	NV: "Nevada",
	NH: "New Hampshire",
	NJ: "New Jersey",
	NM: "New Mexico",
	NY: "New York",
	NC: "North Carolina",
	ND: "North Dakota",
	OH: "Ohio",
	OK: "Oklahoma",
	OR: "Oregon",
	PA: "Pennsylvania",
	RI: "Rhode Island",
	SC: "South Carolina",
	SD: "South Dakota",
	TN: "Tennessee",
	TX: "Texas",
	UT: "Utah",
	VT: "Vermont",
	VA: "Virginia",
	WA: "Washington",
	WV: "West Virginia",
	WI: "Wisconsin",
	WY: "Wyoming",
}
